import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";

interface Customer {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  emailVerified: boolean;
  createdAt: string;
}

interface CustomerList {
  items: Customer[];
  total: number;
  page: number;
  limit: number;
}

const LIMIT = 20;

function fmtDate(iso: string) {
  return iso.slice(0, 10).replace(/-/g, ".");
}

function fullName(c: Customer) {
  return [c.firstName, c.lastName].filter(Boolean).join(" ");
}

export function CustomersPage() {
  const navigate = useNavigate();
  const { user, can } = useAuth();

  const [items, setItems] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && !can("user:view")) navigate({ to: "/dpps" });
  }, [user, can, navigate]);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    api
      .get<CustomerList>("/admin/users", { params: { page, limit: LIMIT } })
      .then(({ data }) => {
        setItems(data.items);
        setTotal(data.total);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load customers");
        setLoading(false);
      });
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const from = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const to = Math.min(page * LIMIT, total);

  return (
    <div className="adm__body">
      <div className="adm__page-head">
        <div className="adm__title-stack">
          <div className="meta">↗ ADMIN CONSOLE CUSTOMERS</div>
          <h1 className="h1" style={{ fontSize: 36, margin: 0 }}>
            Customers
          </h1>
        </div>
      </div>

      {error && (
        <div
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 12,
            color: "var(--danger)",
            border: "1px solid var(--danger)",
            padding: "10px 14px",
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Verified</TableHead>
            <TableHead>Joined</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} style={{ padding: 32 }}>
                Loading…
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} style={{ padding: 32 }}>
                No customers
              </TableCell>
            </TableRow>
          ) : (
            items.map((c) => (
              <TableRow
                key={c.id}
                style={{ cursor: "pointer" }}
                onClick={() =>
                  navigate({ to: "/customer", search: { id: c.id } })
                }
              >
                <TableCell>{fullName(c) || "—"}</TableCell>
                <TableCell>
                  <span className="mono">{c.email ?? "—"}</span>
                </TableCell>
                <TableCell className="mono">{c.phone ?? "—"}</TableCell>
                <TableCell>
                  <Badge variant={c.emailVerified ? "success" : "mute"}>
                    {c.emailVerified ? "verified" : "unverified"}
                  </Badge>
                </TableCell>
                <TableCell className="mono">{fmtDate(c.createdAt)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 16,
          fontFamily: "var(--f-mono)",
          fontSize: 12,
          color: "var(--ink-mute)",
        }}
      >
        <span>
          {total === 0 ? "0 customers" : `Showing ${from}–${to} of ${total}`}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            variant="ghost"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={page >= pages || loading}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
